import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Hash, Lock, Plus, Loader2, Settings, Trash2 } from "lucide-react";
import { useAuth } from "../scripts/AuthContext";
import {
  createTeamChannel,
  listTeamChannels,
  joinChannel,
  updateChannel,
  deleteChannel,
} from "@/lib/dmApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toastError, toastSuccess } from "@/lib/toast";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import ChannelPostingPolicyFields from "@/components/dm/ChannelPostingPolicyFields";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Team page: list channels + admin create/rename/soft-delete (restricted-team → org-wide warning).
 */
export default function TeamChannelsPanel({
  orgId,
  teamId,
  teamVisibility,
  isTeamAdmin,
}) {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [searchable, setSearchable] = useState(true);
  const [postingPolicy, setPostingPolicy] = useState("open");
  const [allowedPosterIds, setAllowedPosterIds] = useState([]);
  const [allowedPosterUsers, setAllowedPosterUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [editPolicy, setEditPolicy] = useState("open");
  const [editPosterIds, setEditPosterIds] = useState([]);
  const [editPosterUsers, setEditPosterUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  const teamIsRestricted = teamVisibility === "restricted";
  const currentUserId = useMemo(() => {
    try {
      if (!accessToken) return null;
      return jwtDecode(accessToken)?.sub || null;
    } catch {
      return null;
    }
  }, [accessToken]);

  useEffect(() => {
    if (visibility === "public" && teamIsRestricted) {
      setPostingPolicy("team_only");
    } else if (visibility === "private") {
      setPostingPolicy("open");
    }
  }, [visibility, teamIsRestricted]);

  useEffect(() => {
    if (postingPolicy !== "custom") return;
    if (allowedPosterIds.length > 0 || !currentUserId) return;
    setAllowedPosterIds([currentUserId]);
  }, [postingPolicy, allowedPosterIds.length, currentUserId]);

  const refresh = async () => {
    if (!accessToken || !orgId || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await listTeamChannels(accessToken, orgId, teamId);
      setChannels(list);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load channels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, orgId, teamId]);

  const openChannel = async (ch) => {
    const path = `/o/${encodeURIComponent(orgId)}/messages/${ch.conversation_id}`;
    if (ch.is_member) {
      navigate(path, { state: { channel: ch } });
      return;
    }
    navigate(path, {
      state: {
        channel: {
          ...ch,
          is_member: false,
          can_preview: ch.visibility === "public",
        },
      },
    });
  };

  const handleJoin = async (ch, e) => {
    e?.stopPropagation?.();
    try {
      const joined = await joinChannel(accessToken, orgId, ch.conversation_id);
      navigate(
        `/o/${encodeURIComponent(orgId)}/messages/${joined.conversation_id}`,
        { state: { channel: joined } }
      );
    } catch (err) {
      toastError(err, "Could not join");
    }
  };

  const handleCreate = async () => {
    if (!title.trim() || saving) return;
    if (postingPolicy === "custom" && allowedPosterIds.length === 0) {
      toastError("Select at least one person who can post");
      return;
    }
    setSaving(true);
    try {
      const created = await createTeamChannel(accessToken, orgId, teamId, {
        title: title.trim(),
        visibility,
        searchable: visibility === "private" ? searchable : true,
        posting_policy: postingPolicy,
        ...(postingPolicy === "custom"
          ? { allowed_poster_ids: allowedPosterIds }
          : {}),
      });
      setCreateOpen(false);
      setTitle("");
      setVisibility("private");
      setSearchable(true);
      setPostingPolicy("open");
      setAllowedPosterIds([]);
      setAllowedPosterUsers([]);
      await refresh();
      navigate(
        `/o/${encodeURIComponent(orgId)}/messages/${created.conversation_id}`,
        { state: { channel: created } }
      );
    } catch (err) {
      toastError(err, "Could not create channel");
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async () => {
    if (!renameTarget || busy) return;
    const titleTrimmed = renameValue.trim();
    const canRenameTitle = !renameTarget.is_default;
    if (canRenameTitle && !titleTrimmed) return;
    if (editPolicy === "custom" && editPosterIds.length === 0) {
      toastError("Select at least one person who can post");
      return;
    }

    const patch = {};
    if (canRenameTitle && titleTrimmed !== (renameTarget.title || "").trim()) {
      patch.title = titleTrimmed;
    }
    const prevPolicy = renameTarget.posting_policy || "open";
    const prevPosters = renameTarget.allowed_poster_ids || [];
    if (editPolicy !== prevPolicy) {
      patch.posting_policy = editPolicy;
    }
    if (editPolicy === "custom") {
      const samePosters =
        editPosterIds.length === prevPosters.length &&
        editPosterIds.every((id) => prevPosters.includes(id));
      if (!samePosters || editPolicy !== prevPolicy) {
        patch.posting_policy = editPolicy;
        patch.allowed_poster_ids = editPosterIds;
      }
    }
    if (!Object.keys(patch).length) {
      setRenameTarget(null);
      return;
    }

    setBusy(true);
    try {
      await updateChannel(
        accessToken,
        orgId,
        renameTarget.conversation_id,
        patch
      );
      toastSuccess("Channel updated");
      setRenameTarget(null);
      await refresh();
    } catch (err) {
      toastError(err, "Could not update channel");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (ch, e) => {
    e?.stopPropagation?.();
    if (busy) return;
    const ok = await confirm({
      title: "Remove channel?",
      description: `“${ch.title}” will be removed from active channels. Message history is retained for a future archive — it won’t show in lists until restored.`,
      confirmLabel: "Remove channel",
      variant: "destructive",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteChannel(accessToken, orgId, ch.conversation_id);
      toastSuccess("Channel removed");
      await refresh();
    } catch (err) {
      toastError(err, "Could not remove channel");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Channels</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Team chat lives here. Posts and updates stay on the Feed.
          </p>
        </div>
        {isTeamAdmin && (
          <Button
            type="button"
            size="sm"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            New channel
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : channels.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {isTeamAdmin
            ? "No channels yet."
            : "No channels yet. Ask a team admin to create one, or open Channel Browser to join organization-wide channels."}
        </p>
      ) : (
        <ul className="divide-y border rounded-md">
          {channels.map((ch) => {
            const isOrgWide = ch.visibility === "public";
            const warnOrgWide = isOrgWide && teamIsRestricted;
            const canManageRow =
              isTeamAdmin && (ch.can_manage !== false || ch.is_default);
            const canRename = canManageRow && !ch.is_default;
            const canEditSettings = canManageRow;
            return (
              <li key={ch.conversation_id}>
                <div className="flex items-center gap-1 pr-1">
                  <button
                    type="button"
                    onClick={() => openChannel(ch)}
                    className="min-w-0 flex-1 flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted shrink-0">
                      {isOrgWide ? (
                        <Hash className="h-4 w-4" />
                      ) : (
                        <Lock className="h-4 w-4" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-medium truncate">
                          {ch.title}
                        </span>
                        {ch.is_default && (
                          <Badge variant="secondary" className="text-[10px] h-5">
                            Default
                          </Badge>
                        )}
                        {ch.posting_policy === "team_only" && (
                          <Badge variant="outline" className="text-[10px] h-5">
                            Team posts only
                          </Badge>
                        )}
                        {ch.posting_policy === "custom" && (
                          <Badge variant="outline" className="text-[10px] h-5">
                            Custom posters
                          </Badge>
                        )}
                        {warnOrgWide ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] h-5 border-amber-500/50 text-amber-700 dark:text-amber-400"
                          >
                            Organization-wide
                          </Badge>
                        ) : !isOrgWide ? (
                          <Badge variant="outline" className="text-[10px] h-5">
                            Restricted
                          </Badge>
                        ) : null}
                      </div>
                      {warnOrgWide && (
                        <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5">
                          Visible to everyone in the org
                        </p>
                      )}
                    </div>
                    {isOrgWide && !ch.is_member && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs shrink-0"
                        onClick={(e) => handleJoin(ch, e)}
                      >
                        Join
                      </Button>
                    )}
                  </button>
                  {canEditSettings && (
                    <div className="flex items-center gap-0.5 shrink-0 pr-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              aria-label="Channel settings"
                              disabled={busy}
                              onClick={(e) => {
                                e.stopPropagation();
                                setRenameTarget(ch);
                                setRenameValue(ch.title || "");
                                setEditPolicy(ch.posting_policy || "open");
                                setEditPosterIds(ch.allowed_poster_ids || []);
                                setEditPosterUsers(ch.allowed_posters || []);
                              }}
                            >
                              <Settings className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Channel settings</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      {canRename && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive"
                          title="Remove channel"
                          disabled={busy}
                          onClick={(e) => handleDelete(ch, e)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create channel</DialogTitle>
            <DialogDescription>
              Extra channels for this team. Default channel already exists for
              all members.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="ch-title">Name</Label>
              <Input
                id="ch-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. standups"
                maxLength={80}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Visibility</Label>
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  size="sm"
                  variant={visibility === "public" ? "default" : "outline"}
                  onClick={() => setVisibility("public")}
                >
                  Organization-wide
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={visibility === "private" ? "default" : "outline"}
                  onClick={() => setVisibility("private")}
                >
                  Restricted
                </Button>
              </div>
              {teamIsRestricted && visibility === "public" && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  This team is restricted, but an organization-wide channel is{" "}
                  <strong>visible to everyone in the organization</strong>.
                </p>
              )}
            </div>
            <ChannelPostingPolicyFields
              accessToken={accessToken}
              orgId={orgId}
              postingPolicy={postingPolicy}
              onPostingPolicyChange={setPostingPolicy}
              selectedIds={allowedPosterIds}
              selectedUsers={allowedPosterUsers}
              onSelectedIdsChange={(ids, users) => {
                setAllowedPosterIds(ids);
                if (users) setAllowedPosterUsers(users);
              }}
            />
            {visibility === "private" && (
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={searchable}
                    onChange={(e) => setSearchable(e.target.checked)}
                  />
                  Show in channel browser (searchable)
                </label>
                <p className="text-xs text-muted-foreground pl-6">
                  Discoverable in the browser, but invite-only — others cannot
                  join without an invitation.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleCreate}
              disabled={
                saving ||
                !title.trim() ||
                (postingPolicy === "custom" && allowedPosterIds.length === 0)
              }
            >
              {saving ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!renameTarget}
        onOpenChange={(o) => !o && setRenameTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Channel settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!renameTarget?.is_default && (
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  maxLength={80}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleRename();
                    }
                  }}
                />
              </div>
            )}
            <ChannelPostingPolicyFields
              accessToken={accessToken}
              orgId={orgId}
              postingPolicy={editPolicy}
              onPostingPolicyChange={(next) => {
                setEditPolicy(next);
                if (
                  next === "custom" &&
                  editPosterIds.length === 0 &&
                  currentUserId
                ) {
                  setEditPosterIds([currentUserId]);
                }
              }}
              selectedIds={editPosterIds}
              selectedUsers={editPosterUsers}
              onSelectedIdsChange={(ids, users) => {
                setEditPosterIds(ids);
                if (users) setEditPosterUsers(users);
              }}
            />
            <Button
              type="button"
              disabled={
                busy ||
                (!renameTarget?.is_default && !renameValue.trim()) ||
                (editPolicy === "custom" && editPosterIds.length === 0)
              }
              onClick={handleRename}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
